import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import Typography from "components/typography";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { editVehicleTypePageRouteName } from "../edit/edit.page";
import {
  useVehicleTypeServiceV1GetVehicleTypeQuery,
  useVehicleTypeServiceV1ListVehicleTypeCategoriesQuery,
} from "_services/modules/vehicle-type";

export const detailVehicleTypePageRouteName = (id?: string) =>
  `/master-data/vehicle-type/${id ?? ":id"}/detail`;

const DetailVehicleTypePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation("vehicleType");

  const { data: detail, isLoading } = useVehicleTypeServiceV1GetVehicleTypeQuery(
    { id: id! },
    { skip: !id }
  );

  const { data: categoriesData } = useVehicleTypeServiceV1ListVehicleTypeCategoriesQuery();

  const config = useMemo(() => {
    const category = categoriesData?.vehicle_type_categories?.find(
      (c) => c.id === detail?.category_id
    );
    return {
      hasTempType: category?.has_temp_type ?? false,
      hasChassis: category?.has_chassis ?? false,
      hasContainer: category?.has_container ?? false,
    };
  }, [detail?.category_id, categoriesData]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-detail"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Typography variant="bodySmall" tone="muted">
          Loading...
        </Typography>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex items-center justify-center py-16">
        <Typography variant="bodySmall" tone="muted">
          Data not found
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between mb-6">
          <Typography variant="heading4" as="h2">
            {t("detail-section-title")}
          </Typography>
          <Button
            type="button"
            variant="fill"
            color="primary"
            size="small"
            iconLeft={<Icon name="edit-2" size={14} />}
            onClick={() =>
              navigate(editVehicleTypePageRouteName(id))
            }
            data-testid="btn-edit-detail-type"
          >
            {t("btn-edit")}
          </Button>
        </div>
      <section className="rounded-2xl border border-border bg-white p-6 shadow-sm"> 
        <Typography variant="heading4" className="mb-6">
          {detail.vehicle_type}
        </Typography>

        <div className="grid grid-cols-4 gap-6">
          {/* Vehicle Category */}
          <div className="flex flex-col gap-1">
            <Typography variant="body" >
              {t("label-vehicle-category")}
            </Typography>
            <Typography variant="body" tone="muted">
              {detail.category_name ?? "—"}
            </Typography>
          </div>

          {/* Temp Type - conditional */}
          {config.hasTempType && (
            <div className="flex flex-col gap-1">
              <Typography variant="body" >
                {t("label-temp-option")}
              </Typography>
              <Typography variant="body" tone="muted">
                {detail.temp_type_name ?? "—"}
              </Typography>
            </div>
          )}

          {/* Chassis - conditional */}
          {config.hasChassis && (
            <div className="flex flex-col gap-1">
              <Typography variant="body" >
                {t("label-is-chassis")}
              </Typography>
              <Typography variant="body" tone="muted">
                {detail.is_chassis ? "Yes" : "—"}
              </Typography>
            </div>
          )}

          {/* Container - conditional */}
          {config.hasContainer && (
            <div className="flex flex-col gap-1">
              <Typography variant="body">
                {t("label-is-container")}
              </Typography>
              <Typography variant="body" tone="muted">
                {detail.is_container ? "Yes" : "—"}
              </Typography>
            </div>
          )}

          {/* Status */}
          <div className="flex flex-col gap-1">
            <Typography variant="body">
              {t("label-status")}
            </Typography>
            <Typography
              variant="body"
              tone="muted"
              className={
                detail.status === "active"
                  ? "text-primary-600 capitalize"
                  : "text-greyScale-60 capitalize"
              }
            >
              {detail.status}
            </Typography>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DetailVehicleTypePage;
